import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { LogOut, LayoutDashboard, CalendarCheck2, ImageIcon, Sparkles, Printer, UtensilsCrossed, Users, BellRing, BookOpenText, KeyRound, CalendarDays, IdCard, ReceiptText, MessageCircle, Package, ChefHat, Soup, CalendarX, TrendingUp, Calculator, Database, Truck, LineChart, Boxes } from "lucide-react";

const NAV_GROUPS = [
  {
    label: "Overview",
    items: [
      { to: "/staff", label: "Dashboard", icon: LayoutDashboard, end: true },
      { to: "/staff/calendar", label: "Calendar", icon: CalendarDays },
    ],
  },
  {
    label: "Reservations",
    items: [
      { to: "/staff/reservations", label: "Reservations", icon: CalendarCheck2 },
      { to: "/staff/slot-blocks", label: "Availability", icon: CalendarX, adminOnly: true },
      { to: "/staff/reminders", label: "Reminders", icon: BellRing },
      { to: "/staff/whatsapp-log", label: "WhatsApp Log", icon: MessageCircle },
    ],
  },
  {
    label: "Menu",
    items: [
      { to: "/staff/menu", label: "Guest Menu", icon: BookOpenText },
      { to: "/staff/staff-menu", label: "Staff Menu", icon: ChefHat },
      { to: "/staff/staff-meals", label: "Staff Meals", icon: Soup },
      { to: "/staff/party-menu", label: "Party Menu", icon: UtensilsCrossed },
      { to: "/staff/recipes", label: "Recipes", icon: Calculator },
    ],
  },
  {
    label: "Operations",
    items: [
      { to: "/staff/kitchen-brief", label: "Kitchen Brief", icon: Printer },
      { to: "/staff/kots", label: "KOTs", icon: ReceiptText },
      { to: "/staff/gallery", label: "Gallery", icon: ImageIcon },
      { to: "/staff/events", label: "Events", icon: Sparkles },
    ],
  },
  {
    label: "Procurement",
    items: [
      { to: "/staff/procurement", label: "Procurement", icon: Package },
      { to: "/staff/inventory", label: "Inventory", icon: Boxes },
      { to: "/staff/procurement-intelligence", label: "Proc. Intel", icon: LineChart },
      { to: "/staff/product-out", label: "Product Out", icon: Truck },
      { to: "/staff/vendor-performance", label: "Vendor perf", icon: TrendingUp },
    ],
  },
  {
    label: "Team & System",
    items: [
      { to: "/staff/employees", label: "Employees", icon: IdCard, hrOnly: true },
      { to: "/staff/users", label: "Users", icon: Users },
      { to: "/staff/backups", label: "Backups", icon: Database, adminOnly: true },
    ],
  },
];

export default function StaffLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  return (
    <div data-testid="staff-layout" className="min-h-screen bg-[hsl(var(--bg))]">
      <header className="bg-[hsl(var(--forest-2))] text-[hsl(var(--bg))] print:hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/staff" className="display text-xl">
            TBB <span className="italic text-[hsl(var(--brass))]">back office</span>
          </Link>
          <div className="flex items-center gap-4 text-sm">
            <span className="text-[hsl(var(--bg))]/70 hidden md:inline">
              Signed in as <span className="text-[hsl(var(--brass))]">{user?.email}</span>
            </span>
            <Link to="/staff/change-password" data-testid="staff-change-password" className="inline-flex items-center gap-1.5 text-[hsl(var(--bg))]/80 hover:text-[hsl(var(--brass))]">
              <KeyRound size={14} /> Password
            </Link>
            <Link to="/" data-testid="staff-view-site" className="text-[hsl(var(--bg))]/80 hover:text-[hsl(var(--brass))]">
              View site
            </Link>
            <button
              data-testid="staff-logout"
              onClick={() => { logout(); nav("/login", { replace: true }); }}
              className="inline-flex items-center gap-1.5 text-[hsl(var(--bg))]/80 hover:text-[hsl(var(--brass))]"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-10">
        <aside className="print:hidden">
          {/*
            Sidebar has more nav items than fit the viewport. Capping the height
            and making it scroll independently prevents scroll-lock on the main
            content area (previously the sticky sidebar swallowed wheel events).
          */}
          <nav
            className="flex lg:flex-col gap-3 lg:sticky lg:top-24 overflow-x-auto lg:overflow-x-visible lg:overflow-y-auto lg:max-h-[calc(100vh-7rem)] lg:pr-1 lg:overscroll-contain"
          >
            {NAV_GROUPS.map((group) => {
              const items = group.items
                .filter((n) => !n.hrOnly || user?.role === "admin" || user?.role === "manager")
                .filter((n) => !n.adminOnly || user?.role === "admin");
              if (items.length === 0) return null;
              return (
                <div key={group.label} className="flex lg:flex-col gap-1 lg:mb-1">
                  <div className="hidden lg:block px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-[hsl(var(--brass-2))]">
                    {group.label}
                  </div>
                  {items.map((n) => (
                    <NavLink
                      key={n.to}
                      to={n.to}
                      end={n.end}
                      data-testid={`staff-nav-${n.label.toLowerCase().replace(/\s+/g, "-").replace(/\./g, "")}`}
                      className={({ isActive }) =>
                        `flex items-center gap-2 px-3 py-2 rounded text-sm whitespace-nowrap transition-colors ${
                          isActive
                            ? "bg-[hsl(var(--forest))] text-[hsl(var(--bg))]"
                            : "text-[hsl(var(--ink-soft))] hover:bg-[hsl(var(--bg-2))]"
                        }`
                      }
                    >
                      <n.icon size={15} /> {n.label}
                    </NavLink>
                  ))}
                </div>
              );
            })}
          </nav>
        </aside>

        <main>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
